import {
  useParams,
  useNavigate,
  Link,
  Outlet,
  NavLink,
} from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { useEffect, useState } from "react";
import CasesApi from "../../../api/cases";

import { useTheme } from "styled-components";
import { updateCase } from "../../../api";

import { db_timestamp } from "../../../utils/date-format";
import { useAuth } from "../../../contexts/AuthContext";
import { addRecentCase } from "../../../api/cases/recentCases";
import { usePermissions } from "../../../hooks/usePermissions";
import CaseMoreButton from "./CaseMoreButton";
import KeyboardReturnOutlinedIcon from "@mui/icons-material/KeyboardReturnOutlined";
import { useQueries, useQueryClient } from "@tanstack/react-query";
import TaskButton from "../../../Monolith-UI/TaskButton.js";
import EditCaseModal from "../components/EditCaseModal.js";
import styled from "styled-components";
import DropdownMenu from "../../../Monolith-UI/DropdownMenu/DropdownMenu.js";
import { FeatureFlagResolver, Features } from "../../../FeatureFlagResolver";
import { Case } from "../types/Cases";

import CaseStatusApi from "@/api/case-status";
import CaseTypesApi from "@/api/case-types";
import { DeleteCaseModal } from "@/components/Modals";
import LoggingAPI from "@/api/logging";
import { CaseMetaBadge, CaseProvider } from "@/Features";
import { Button } from "@monolith-forensics/monolith-ui";
import { useFeatures } from "@/contexts/FeaturesContext";

interface CaseStatusItem {
  case_status_id: number;
  status_name: string;
  total: number;
}

export interface CaseTypeItem {
  case_type_id: number;
  case_type: string;
  total: number;
}

interface CaseHeader {
  className?: string;
  caseInfo: Case;
  handleCaseInfoUpdate: (
    data: Partial<Case>,
    options: { updateDB: boolean }
  ) => void;
  statusItems: CaseStatusItem[];
  caseTypeItems: CaseTypeItem[];
}

const tabs = [
  { label: "Overview", value: "overview" },
  { label: "Evidence", value: "evidence" },
  { label: "Acquisitions", value: "acquisitions" },
  { label: "Storage Items", value: "case-storage" },
  { label: "Analysis", value: "analysis" },
  { label: "eDiscovery", value: "ediscovery" },
  { label: "Tasks", value: "tasks" },
  { label: "Notes", value: "notes" },
  { label: "Files", value: "documents" },
  { label: "Contacts", value: "contacts" },
  { label: "Quality Assurance", value: "qa" },
  { label: "Reports", value: "reports" },
  { label: "Activity", value: "activity" },
];

const Tabs = styled(({ className }) => {
  const { currentUser, workspace } = useAuth();
  const { hasFeatures, FEATURES } = useFeatures();
  const hasEdiscoveryFeatures = hasFeatures([FEATURES.EDISCOVERY_ALL_FEATURES]);

  const showTimelineSection = FeatureFlagResolver(
    currentUser.email,
    workspace.tenant_slug,
    Features.CASE_ANALYSIS
  );

  return (
    <div className={className}>
      <div className="tabs-container">
        {tabs
          .filter((tab) => tab.value !== "analysis" || showTimelineSection)
          .filter((tab) => tab.value !== "ediscovery" || hasEdiscoveryFeatures)
          .map((tab) => (
            <NavLink to={`${tab.value}`} key={tab.value} data-testid="case-tab">
              <div className="tab">{tab.label}</div>
            </NavLink>
          ))}
      </div>
      <div className="border-overlay"></div>
    </div>
  );
})`
  margin-top: 5px;
  margin-bottom: 10px;

  .border-overlay {
    border-bottom: 1px solid ${(props) => props.theme.palette.divider};
  }

  .tabs-container {
    display: flex;
    flex-direction: row;
    color: ${({ theme }) => theme.palette.text.secondary};
  }

  .tab {
    padding: 4px 8px;
    padding-bottom: 2px;
    border-bottom: 1px solid transparent;
  }

  .tab:hover {
    background-color: ${({ theme }) => theme.palette.action.hover};
  }

  a.active .tab {
    border-bottom-color: ${({ theme }) => theme.palette.primary.main};
    color: ${({ theme }) => theme.palette.primary.main};
  }
`;

const TabSection = styled(({ className }) => {
  return (
    <div className={className}>
      <Tabs />
      <div className="tab-content">
        <Outlet />
      </div>
    </div>
  );
})`
  display: flex;
  flex-direction: column;
  flex-grow: 1;

  .tab-content {
    display: flex;
    flex-direction: column;
    flex-grow: 1;
  }
`;

const CaseHeader = styled(
  ({
    className,
    caseInfo,
    handleCaseInfoUpdate,
    statusItems,
    caseTypeItems,
  }: CaseHeader) => {
    const queryClient = useQueryClient();
    const theme: any = useTheme();
    const { hasPermission } = usePermissions();
    const navigate = useNavigate();
    const [showEditPopup, setShowEditPopup] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);

    const handleDeleteCase = () => {
      setShowDeleteModal(true);
    };

    const handleChangeStatus = (data: { case_status: string }) => {
      updateCase(caseInfo?.case_id, {
        case_status: data.case_status,
        case_closed_date: data.case_status === "Closed" ? db_timestamp() : null,
      });
      queryClient.setQueryData(
        [
          "cases",
          "list",
          {
            case_id: caseInfo?.case_id,
          },
        ],
        (prevData: Case[] | undefined) => {
          if (!prevData) {
            return [];
          }
          const [oldCaseData] = prevData;
          return [
            {
              ...oldCaseData,
              case_status: data.case_status,
              case_closed_date:
                data.case_status === "Closed" ? db_timestamp() : null,
            },
          ];
        }
      );
    };

    const handleChangeType = (data: { case_type: string }) => {
      updateCase(caseInfo?.case_id, {
        case_type: data.case_type,
      });
      queryClient.setQueryData(
        [
          "cases",
          "list",
          {
            case_id: caseInfo?.case_id,
          },
        ],
        (prevData: Case[] | undefined) => {
          if (!prevData) {
            return [];
          }
          const [oldCaseData] = prevData;
          return [
            {
              ...oldCaseData,
              case_type: data.case_type,
            },
          ];
        }
      );
    };

    return (
      <div className={className}>
        <TaskButton
          onClick={() => navigate("/cases")}
          style={{ outline: `1px solid ${theme.palette.divider}` }}
          title="Back to Cases"
        >
          <KeyboardReturnOutlinedIcon style={{ fontSize: 16 }} />
        </TaskButton>
        <div className="case-name">{caseInfo?.case_ref}</div>
        {caseInfo && <CaseMetaBadge caseInfo={caseInfo} />}
        <DropdownMenu
          menuItems={(statusItems || []).map((item) => ({
            label: item.status_name,
            value: item.status_name,
            onClick: () =>
              handleChangeStatus({ case_status: item.status_name }),
          }))}
          defaultValue={caseInfo?.case_status || "Select Case Status..."}
          variant="text"
          arrow
          disabled={!hasPermission()}
          buttonProps={{
            style: {
              color: theme.palette.primary.main,
              fontSize: 12,
              fontWeight: 600,
            },
          }}
          children={null}
          dropdownContent={null}
        />
        <DropdownMenu
          menuItems={(caseTypeItems || []).map((item) => ({
            label: item.case_type,
            value: item.case_type,
            onClick: () => handleChangeType({ case_type: item.case_type }),
          }))}
          defaultValue={caseInfo?.case_type || "Select Case Type..."}
          variant="text"
          arrow
          disabled={!hasPermission()}
          buttonProps={{
            style: {
              color: theme.palette.primary.main,
              fontSize: 12,
              fontWeight: 600,
            },
          }}
          children={null}
          dropdownContent={null}
        />
        {hasPermission() && (
          <CaseMoreButton
            handleEditCase={() => {
              setShowEditPopup(true);
            }}
            handleDeleteCase={handleDeleteCase}
          />
        )}
        <EditCaseModal
          caseInfo={caseInfo}
          open={showEditPopup}
          handleClose={() => setShowEditPopup(false)}
          onSubmit={(data) => handleCaseInfoUpdate(data, { updateDB: false })}
        />
        <DeleteCaseModal
          show={showDeleteModal}
          onClose={() => setShowDeleteModal(false)}
          onCancel={() => setShowDeleteModal(false)}
          onSubmit={() => {
            navigate("/cases");
          }}
          defaultInfo={caseInfo}
        />
      </div>
    );
  }
)`
  display: flex;
  flex-direction: row;
  align-items: center;
  padding: 0px 10px;

  .case-name {
    font-size: 1.2rem;
    font-weight: bold;
    max-width: 500px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    margin-right: 10px;
    margin-left: 5px;
  }

  .case-number {
    font-size: 0.65rem;
    color: ${(props) => props.theme.palette.text.secondary};
    font-weight: 600;
  }
`;

const CaseDetails = styled(({ className }) => {
  const theme: any = useTheme();
  const { case_id } = useParams();
  const { currentUser } = useAuth();
  const queryClient = useQueryClient();

  const result = useQueries({
    queries: [
      {
        queryKey: [
          "cases",
          "list",
          {
            case_id: case_id ? parseInt(case_id) : null,
          },
        ],
        queryFn: () =>
          CasesApi.getCases({
            case_id: case_id ? parseInt(case_id) : null,
          }),
      },
      {
        queryKey: ["cases:statuses"],
        queryFn: () => CaseStatusApi.getCaseStatuses(),
      },
      {
        queryKey: ["cases:types"],
        queryFn: () => CaseTypesApi.getCaseTypes(),
      },
    ],
  });

  const handleCaseInfoUpdate = async (
    data: Partial<Case> = {},
    options = { updateDB: false }
  ) => {
    if (options.updateDB !== false) await updateCase(caseInfo.case_id, data);

    queryClient.refetchQueries({
      queryKey: [
        "cases",
        "list",
        {
          case_id: case_id ? parseInt(case_id) : null,
        },
      ],
    });
  };

  const isDone = result.every((item) => item.isFetched);

  const [caseInfo] = result[0]?.data || [];
  const caseUUID = caseInfo?.uuid || null;
  const statusItems = result[1].data || null;
  const caseTypeItems = result[2].data || null;

  useEffect(() => {
    if (!caseUUID) return;

    addRecentCase({
      case_id,
      user_id: currentUser.user_id,
    });

    // Log user access to case
    LoggingAPI.createLog({
      case_id: case_id ? parseInt(case_id) : null,
      user_id: currentUser.user_id,
      message: `User viewed case.`,
      event: "case:view",
      object_type: "case",
      object_uuid: caseUUID,
    });
  }, [caseUUID, case_id, currentUser.user_id]);

  if (!case_id) {
    return <div>No case ID found</div>;
  }

  return (
    <CaseProvider case_id={parseInt(case_id)}>
      <div className={className}>
        {isDone && caseInfo && (
          <>
            <Helmet>
              <title>Case Details | {caseInfo.case_number}</title>
            </Helmet>
            <CaseHeader
              caseInfo={caseInfo}
              handleCaseInfoUpdate={handleCaseInfoUpdate}
              statusItems={statusItems}
              caseTypeItems={caseTypeItems}
            />
            <TabSection caseInfo={caseInfo} />
          </>
        )}
        {isDone && !caseInfo && (
          <>
            <Helmet>
              <title>Case Details | Access Denied</title>
            </Helmet>
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
                alignItems: "center",
                marginTop: 100,
                padding: 15,
                border: `1px solid ${theme.palette.divider}`,
                borderRadius: 5,
                width: "fit-content",
                margin: "auto",
              }}
            >
              <div style={{ fontSize: 24, fontWeight: "bold" }}>
                You do not have access to this case.
              </div>
              <div
                style={{ color: theme.palette.text.secondary, fontSize: 18 }}
              >
                Request access from the case lead or Monolith admin.
              </div>
              <Link to="/cases">
                <Button
                  variant="contained"
                  color="primary"
                  style={{ marginTop: 20 }}
                >
                  Return To Cases List
                </Button>
              </Link>
            </div>
          </>
        )}
      </div>
    </CaseProvider>
  );
})`
  display: flex;
  flex-direction: column;
  flex-grow: 1;
`;

export default CaseDetails;
